import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Special popup for deleting bin with batches information.
 * Shows batches assigned to bin and options to move or delete.
 */
const BinDeletePopUp = ({
  open = false,
  onClose,
  onDeleteWithBatches,
  onMoveBatches,
  binCode = '',
  batches = [],
  availableBins = [],
  loading = false,
}) => {
  const [selectedBinId, setSelectedBinId] = useState('');
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedBinId(''); // Reset when popup closes
      return;
    }
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!open) return null;

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999, fontFamily: "'Nunito', sans-serif" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bin-delete-popup-title"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with cross */}
        <div className="flex items-center justify-end pr-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-2">
          <h3 id="bin-delete-popup-title" className="text-lg font-semibold text-gray-900 mb-3">
            Cannot Delete Bin {binCode}
          </h3>
          <p className="text-sm text-gray-700 mb-4">
            {batches.length} batch{batches.length !== 1 ? 'es are' : ' is'} assigned to this bin. 
            Please move these batches to another bin first, then delete the bin.
          </p>

          {/* Batches List */}
          {batches.length > 0 && (
            <div className="mb-4 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
              <p className="text-xs font-semibold text-gray-700 mb-2">Assigned Batches:</p>
              <div className="space-y-1">
                {batches.map((batch, index) => (
                  <div key={index} className="text-xs text-gray-600 flex items-center gap-2">
                    <span className="font-mono font-medium">{batch.batchNumber}</span>
                    <span className="text-gray-400">•</span>
                    <span>{batch.productSku}</span>
                    <span className="text-gray-400">•</span>
                    <span>Qty: {batch.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Move Batches Section */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs font-semibold text-gray-700 mb-2">Move all batches to another bin and delete this bin:</p>
            <select
              value={selectedBinId}
              onChange={(e) => setSelectedBinId(e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 mb-2"
            >
              <option value="">-- Select Bin --</option>
              {availableBins.map((bin) => (
                <option key={bin._id} value={bin._id}>
                  {bin.binCode} ({bin.binType})
                  {/* - Available: {bin.capacity - bin.currentQuantity} */}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                if (selectedBinId && onMoveBatches) {
                  onMoveBatches(selectedBinId);
                  // Don't reset selectedBinId here - let the parent handle reset after operation completes
                } else if (!selectedBinId) {
                  // Show error if no bin selected
                  alert('Please select a target bin first');
                }
              }}
              disabled={!selectedBinId || loading}
              className="w-full h-10 rounded-lg border border-emerald-500 bg-emerald-500 px-4 text-sm font-medium text-white hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Moving Batches and Deleting...' : 'Move Batches and Delete'}
            </button>
          </div>

          <p className="text-xs text-amber-600 mb-4">
            ⚠️ Warning: If you want to delete the bin along with all batches (without moving them), click the button below. This action cannot be undone.
          </p>
        </div>

        {/* Footer - Close / Delete with Batches */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onDeleteWithBatches}
            className="h-10 rounded-full border border-red-500 bg-red-500 px-5 text-sm font-medium text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-2"
          >
            Delete Bin with Batches
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default BinDeletePopUp;
